import { useState, useEffect, useMemo } from 'react';
import { getBannerText, getBannerAnimationDuration } from '../api/woocommerce';

const BannerComponent = ({ bannerSettings, currentLanguage }) => {
  const [isReady, setIsReady] = useState(false);
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Memoize banner text and animation duration to prevent unnecessary recalculations
  const bannerText = useMemo(() =>
    getBannerText(bannerSettings, currentLanguage),
    [bannerSettings, currentLanguage]
  );

  // Calculate responsive animation duration based on screen width
  const animationDuration = useMemo(() => {
    const baseDuration = getBannerAnimationDuration(bannerSettings?.animation_speed);
    const baseSeconds = parseInt(baseDuration);

    // Scale duration based on screen width - smaller screens get proportionally faster animation
    // Desktop (1200px+): 100% speed, Mobile (375px): ~31% speed to maintain same visual speed
    const scaleFactor = Math.max(0.3, screenWidth / 1200);
    const adjustedSeconds = Math.round(baseSeconds * scaleFactor);

    return `${adjustedSeconds}s`;
  }, [bannerSettings?.animation_speed, screenWidth]);

  // Track screen width changes for responsive animation
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!bannerText) return;

    // Simple delay to ensure DOM is ready, then start animation
    setIsReady(false);
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [bannerText]);

  if (!bannerSettings?.enabled || !bannerText) return null;

  return (
    <div
      className="relative overflow-hidden text-white text-14 font-normal pt-[4px] pb-[3px]"
      style={{
        backgroundColor: bannerSettings.bg_color || '#FFFFFF',
        color: bannerSettings.text_color || '#000000',
        opacity: isReady ? 1 : 0.7,
        transition: 'opacity 0.2s ease-in-out'
      }}
    >
      <div
        className={`flex ${isReady ? 'animate-marquee' : ''}`}
        style={{
          animation: isReady ? `marquee ${animationDuration} linear infinite` : 'none'
        }}
      >
        {/* Create a long string of repeated text */}
        {Array(50).fill(bannerText).map((text, index) => (
          <span key={index} className="ml-2 inline-block whitespace-nowrap flex-shrink-0">
            {text}
          </span>
        ))}
      </div>

      {/* CSS for marquee animation */}
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-100%, 0, 0); }
        }
        .animate-marquee {
          animation: marquee ${animationDuration} linear infinite;
        }
      `}</style>
    </div>
  );
};

export default BannerComponent;